// Get quotes for a BOM line: who to ask, then which lines and files go with
// the request, then the draft opens as the preview. Opened from a BOM line
// (the buyer picks the suppliers), from one supplier's row (already picked),
// or from a draft, to go back and change either choice before it is sent.
// On a BOM with builds a package quotes one build's content: the build the
// BOM page showed (?build=), else the buyer picks it here.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/GetQuotesPage.tsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Checkbox, CircularProgress, IconButton, Stack, TextField, Typography, alpha, useTheme } from '@mui/material';
import { Check as CheckIcon, ChevronLeft, ChevronRight as ClosedIcon, ExpandMore as OpenIcon } from '@mui/icons-material';
import rfqPolicyService, { PackagePlan, PackagePlanLine } from '../../../services/rfqPolicyService';
import { useBomPageStats } from '../../../hooks/useBomV2';
import QuoteSteps from './QuoteSteps';
import {
  FILE_KINDS,
  PackageSelection,
  emptySelection,
  fileLabel,
  hasChildren,
  isFileSent,
  isFolded,
  isLineKept,
  needsDrawing,
  packageTotals,
  typeLabel,
} from './packagePlanView';

const LABEL_SX = { fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: 'text.disabled' } as const;
/** Above this many lines the package opens folded to its first level. */
const FOLD_ABOVE = 40;

type PlanSupplier = PackagePlan['suppliers'][number];
/** A package asks a supplier; a single part asks for one supplier's offer (the same supplier can offer an alternative too). */
const keyOf = (plan: PackagePlan, s: PlanSupplier): string => (plan.kind === 'part' ? s.partSupplierId ?? s.supplierId : s.supplierId);

const errorText = (err: unknown): string => {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message || e?.message || 'Something went wrong';
};

const GetQuotesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const bomId = params.get('bomId') ?? '';
  const lineItemId = params.get('line') ?? '';
  const fromSupplier = params.get('supplier') ?? '';
  const draftId = params.get('draft') ?? '';
  // The build a package quotes, on a BOM with builds. It lives in the address
  // so a refresh keeps it; picking one here writes it there.
  const buildKey = params.get('build') ?? '';
  const pickBuild = (key: string) => {
    const next = new URLSearchParams(params);
    next.set('build', key);
    setParams(next, { replace: true });
  };

  const [step, setStep] = useState<1 | 2>(draftId ? (params.get('step') === '1' ? 1 : 2) : fromSupplier ? 2 : 1);
  const [chosen, setChosen] = useState<Set<string>>(new Set(fromSupplier ? [fromSupplier] : []));
  const [selection, setSelection] = useState<PackageSelection>(emptySelection);
  const [closed, setClosed] = useState<Set<string> | null>(null);
  const [find, setFind] = useState('');
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const planQuery = useQuery({
    queryKey: ['rfq-package-plan', draftId, bomId, lineItemId, ...(buildKey && !draftId ? [buildKey] : [])],
    queryFn: () =>
      draftId
        ? rfqPolicyService.getDraftPackagePlan(draftId)
        : rfqPolicyService.getPackagePlan({ bomId, lineItemId, ...(buildKey ? { configurationKey: buildKey } : {}) }),
    enabled: Boolean(draftId || (bomId && lineItemId)),
    // A draft is reopened as it is now, not as it was the last time.
    gcTime: draftId ? 0 : undefined,
    // Another build of the same line keeps the page in place while its lines
    // load; anything else (another line, a draft) loads as it always did.
    placeholderData: (previous) => (!draftId && previous?.lineItemId === lineItemId ? previous : undefined),
  });
  const plan: PackagePlan | undefined = planQuery.data;
  const draft = plan?.draft;
  const isPackage = plan?.kind === 'package';

  // The builds the BOM declares (two or more, as the BOM page counts them).
  // A draft keeps the build it was made for, so it is only named.
  const statsQuery = useBomPageStats(draftId ? (plan?.configurationKey ? plan.bomId : undefined) : bomId || undefined);
  const builds = (statsQuery.data?.configurations?.length ?? 0) >= 2 ? statsQuery.data?.configurations ?? [] : [];
  const chosenBuild = builds.find((b) => b.key === (draftId ? plan?.configurationKey : buildKey));
  // A package of a BOM with builds waits for the buyer to say which build.
  const needsBuild = !draftId && builds.length > 0 && !chosenBuild;
  // The build the new drafts quote: what the plan was read for.
  const quoteBuild = plan?.configurationKey ?? chosenBuild?.key;
  // Not ready to prepare while it is still unknown whether the BOM has builds, or another build's lines load.
  const waiting = (!draftId && statsQuery.isLoading) || planQuery.isPlaceholderData;
  // A single part has no lines to choose, but its files are chosen the same way.
  const fileKinds = FILE_KINDS.filter((k) => plan?.lines.some((l) => l.files.some((f) => f.documentType === k.type)));
  const showFiles = isPackage || fileKinds.length > 0;

  // Reopened from a draft: start from the supplier, lines and files it carries.
  const seeded = useRef(false);
  useEffect(() => {
    if (!draft || seeded.current) return;
    seeded.current = true;
    setChosen(new Set([draft.supplierKey]));
    setSelection({
      offLines: new Set(draft.excludeLineItemIds),
      offKinds: new Set(),
      fileChoice: new Map(draft.excludeDocumentIds.map((id) => [id, false])),
    });
  }, [draft]);

  // A large package opens on its first level; the buyer opens what they need.
  const folded = useMemo(() => {
    if (closed) return closed;
    if (!plan || plan.lines.length <= FOLD_ABOVE) return new Set<string>();
    return new Set(plan.lines.filter((l) => l.depth >= 1 && hasChildren(plan, l)).map((l) => l.lineItemId));
  }, [closed, plan]);

  const totals = useMemo(() => (plan ? packageTotals(plan, selection) : undefined), [plan, selection]);
  const attention = useMemo(() => (plan && plan.kind === 'package' ? plan.lines.filter((l) => isLineKept(plan, l, selection) && needsDrawing(l)) : []), [plan, selection]);

  const change = (edit: (next: PackageSelection) => void) =>
    setSelection((prev) => {
      const next: PackageSelection = { offLines: new Set(prev.offLines), offKinds: new Set(prev.offKinds), fileChoice: new Map(prev.fileChoice) };
      edit(next);
      return next;
    });
  const toggle = (set: Set<string>, id: string) => (set.has(id) ? set.delete(id) : set.add(id));

  const prepare = async () => {
    if (!plan || !totals || needsBuild || waiting) return;
    setPreparing(true);
    setFailure(null);
    const created: string[] = [];
    const toCreate = [...chosen].filter((key) => key !== draft?.supplierKey);
    try {
      if (draft && showFiles) {
        await rfqPolicyService.updateDraftPackage(draft.rfqId, {
          excludeLineItemIds: totals.excludeLineItemIds,
          excludeDocumentIds: totals.excludeDocumentIds,
        });
      }
      for (const key of toCreate) {
        if (isPackage) {
          const result = await rfqPolicyService.createBuildToPrintRfq({
            bomId: plan.bomId,
            lineItemId: plan.lineItemId,
            supplierId: key,
            excludeLineItemIds: totals.excludeLineItemIds,
            excludeDocumentIds: totals.excludeDocumentIds,
            ...(quoteBuild ? { configurationKey: quoteBuild } : {}),
          });
          created.push(result.rfqId);
        } else {
          const result = await rfqPolicyService.dispatchRfq({
            lineItemId: plan.lineItemId,
            partSupplierId: key,
            excludeDocumentIds: totals.excludeDocumentIds,
            ...(quoteBuild ? { configurationKey: quoteBuild } : {}),
          });
          created.push(result.rfqId);
        }
      }
    } catch (err) {
      setFailure(created.length ? `${created.length} of ${toCreate.length} new drafts were prepared, then: ${errorText(err)}` : errorText(err));
      setPreparing(false);
      return;
    }
    qc.invalidateQueries({ queryKey: ['rfq-center'] });
    qc.invalidateQueries({ queryKey: ['rfq-detail-page'] });
    qc.invalidateQueries({ queryKey: ['bom-supplier-coverage'] });
    setPreparing(false);
    const openId = draft?.rfqId ?? created[0];
    if (openId) navigate(`/rfq?open=${encodeURIComponent(openId)}&full=1`);
  };

  if (!draftId && (!bomId || !lineItemId)) {
    return <Alert severity="info" sx={{ m: 2 }}>Open this page from a BOM line.</Alert>;
  }
  if (planQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (planQuery.isError || !plan || !totals) {
    return <Alert severity="error" sx={{ m: 2 }}>{errorText(planQuery.error)}</Alert>;
  }

  const anchor = plan.lines[0];
  const chosenCount = chosen.size;

  const pill = (label: string, on: boolean, onClick: () => void, key?: string) => (
    <Box
      key={key ?? label}
      component="button"
      type="button"
      aria-pressed={on}
      onClick={onClick}
      sx={{
        font: 'inherit',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: 28,
        px: 1.5,
        borderRadius: 999,
        fontSize: '0.78rem',
        fontWeight: 600,
        border: `1px solid ${on ? theme.palette.primary.main : theme.palette.divider}`,
        bgcolor: on ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
        color: on ? 'text.primary' : 'text.secondary',
        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
      }}
    >
      {on && <CheckIcon sx={{ fontSize: 14 }} />}
      {label}
    </Box>
  );

  const suppliersStep = (
    <Box sx={{ px: 2.5, py: 2.5 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        Who should we ask?
      </Typography>
      {plan.suppliers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No supplier is linked to this part yet. Add one on the BOM line first.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {plan.suppliers.map((s) => {
            const key = keyOf(plan, s);
            const own = key === draft?.supplierKey;
            const on = chosen.has(key);
            return (
              <Box
                key={key}
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  cursor: 'pointer',
                  border: `1px solid ${on ? theme.palette.primary.main : theme.palette.divider}`,
                  borderRadius: 2.5,
                  bgcolor: 'background.paper',
                }}
              >
                <Checkbox
                  size="small"
                  checked={on}
                  disabled={own}
                  onChange={() =>
                    setChosen((prev) => {
                      const next = new Set(prev);
                      toggle(next, key);
                      return next;
                    })
                  }
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                    {s.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                    {[own ? 'This draft' : s.activeRfqDocumentId ? 'Already has an RFQ for this part' : '', s.offer].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                {s.activeRfqDocumentId && !own && (
                  <Button size="small" onClick={() => navigate(`/rfq?open=${encodeURIComponent(s.activeRfqDocumentId ?? '')}&full=1`)} sx={{ textTransform: 'none' }}>
                    Open
                  </Button>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );

  const term = find.trim().toLowerCase();
  const flat = Boolean(term) || onlyAttention;
  const rows = plan.lines.filter((l) => {
    if (term) return `${l.partNumber ?? ''} ${l.description ?? ''}`.toLowerCase().includes(term);
    if (onlyAttention) return attention.includes(l);
    return !isFolded(plan, l, folded);
  });

  const lineRow = (line: PackagePlanLine) => {
    const kept = isLineKept(plan, line, selection);
    const parent = hasChildren(plan, line);
    return (
      <Box
        key={line.lineItemId}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: showFiles ? 'minmax(0, 1.3fr) 80px minmax(0, 2fr)' : 'minmax(0, 1fr) 80px' },
          gap: { xs: 0.5, md: 2 },
          alignItems: 'center',
          px: 1,
          py: 0.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          '&:first-of-type': { borderTop: 0 },
          opacity: kept ? 1 : 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, pl: flat ? 0 : line.depth * 2.5 }}>
          <IconButton
            size="small"
            aria-label={folded.has(line.lineItemId) ? 'Open' : 'Close'}
            onClick={() => {
              const next = new Set(folded);
              toggle(next, line.lineItemId);
              setClosed(next);
            }}
            sx={{ visibility: parent && !flat ? 'visible' : 'hidden', display: isPackage ? 'inline-flex' : 'none' }}
          >
            {folded.has(line.lineItemId) ? <ClosedIcon fontSize="small" /> : <OpenIcon fontSize="small" />}
          </IconButton>
          <Checkbox
            size="small"
            checked={kept}
            disabled={!isPackage || line.depth === 0 || (!kept && !selection.offLines.has(line.lineItemId))}
            onChange={() => change((next) => toggle(next.offLines, line.lineItemId))}
            inputProps={{ 'aria-label': `Include ${line.partNumber ?? 'line'}` }}
          />
          <Box component="span" sx={{ flex: 'none', mr: 1, px: 0.75, borderRadius: 0.75, fontSize: '0.66rem', fontWeight: 700, color: 'text.secondary', bgcolor: alpha(theme.palette.text.primary, 0.06) }}>
            {typeLabel(line.type)}
          </Box>
          <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {line.partNumber}
            </Box>{' '}
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {line.description}
            </Box>
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', pl: { xs: 9, md: 0 } }}>
          {line.quantity} {line.unit}
        </Typography>
        <Box sx={{ display: showFiles ? 'flex' : 'none', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', pl: { xs: 9, md: 0 }, minWidth: 0 }}>
          {kept &&
            line.files.map((f) => {
              const sent = isFileSent(f, selection);
              return (
                <Box
                  key={f.documentId}
                  component="button"
                  type="button"
                  aria-pressed={sent}
                  title={sent ? 'Click to leave this file out' : 'Click to send this file'}
                  onClick={() => change((next) => next.fileChoice.set(f.documentId, !sent))}
                  sx={{
                    font: 'inherit',
                    cursor: 'pointer',
                    maxWidth: '100%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    border: `1px solid ${sent ? theme.palette.primary.main : theme.palette.divider}`,
                    bgcolor: sent ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    color: sent ? 'text.primary' : 'text.disabled',
                    textDecoration: sent ? 'none' : 'line-through',
                    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                  }}
                >
                  {sent && <CheckIcon sx={{ fontSize: 13 }} />}
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {fileLabel(f)}
                  </Box>
                  {f.revision && (
                    <Box component="span" sx={{ color: 'text.secondary', flex: 'none' }}>
                      Rev {f.revision}
                    </Box>
                  )}
                </Box>
              );
            })}
          {kept && isPackage && needsDrawing(line) && (
            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.warning.dark }}>
              No drawing
            </Typography>
          )}
          {line.type === 'RAW_MATERIAL' && (
            <Typography variant="caption" color="text.secondary">
              Information only
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const linesStep = (
    <Box sx={{ px: 2.5, py: 2.5 }}>
      <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Lines and files
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {isPackage ? 'Everything is already chosen. Untick a line or click a file to leave it out.' : showFiles ? 'One part. Click a file to send it or leave it out.' : 'One part. It has no files to send.'}
        </Typography>
      </Stack>
      <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        {fileKinds.length > 0 && <Typography sx={LABEL_SX}>Send</Typography>}
        {fileKinds.length > 0 &&
          fileKinds.map((k) =>
          pill(k.label, !selection.offKinds.has(k.type), () =>
            change((next) => {
              toggle(next.offKinds, k.type);
              // The switch speaks for every file of its kind again.
              plan.lines.forEach((l) => l.files.forEach((f) => f.documentType === k.type && next.fileChoice.delete(f.documentId)));
            }),
          ),
        )}
        <Box sx={{ flex: 1 }} />
        {plan.lines.length > 8 && (
          <TextField size="small" placeholder="Find a part" value={find} onChange={(e) => setFind(e.target.value)} sx={{ width: 200 }} />
        )}
        {attention.length > 0 && pill(`Needs attention · ${attention.length}`, onlyAttention, () => setOnlyAttention((v) => !v), 'attention')}
      </Stack>
      <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper' }}>
        {needsBuild ? (
          // The lines and quantities depend on the build, so none are shown before it is picked.
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Pick the build above to see its lines.</Typography>
        ) : rows.length ? (
          rows.map(lineRow)
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Nothing matches.</Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, fontVariantNumeric: 'tabular-nums' }}>
        {chosenBuild && `${chosenBuild.label} · `}
        {isPackage
          ? `${totals.lines} ${totals.lines === 1 ? 'line' : 'lines'} · ${totals.files} ${totals.files === 1 ? 'file' : 'files'}. Files are attached to the email, up to ${plan.maxAttachMb} MB in total.`
          : `${showFiles ? `${totals.files} ${totals.files === 1 ? 'file' : 'files'} attached to the email. ` : ''}Quantity and target price are changed on the draft, under Edit.`}
      </Typography>
    </Box>
  );

  return (
    // The same frame as the draft it leads to: moving between the steps only changes what is under them.
    <Box sx={{ width: '100%', pb: 4 }}>
      <Box sx={{ px: 3 }}>
      <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 2, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ px: 2.5, py: 1, alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Button size="small" startIcon={<ChevronLeft />} onClick={() => (draft ? navigate('/rfq') : navigate(-1))} sx={{ textTransform: 'none', fontWeight: 700, color: 'text.primary' }}>
            {draft ? 'RFQ Center' : 'Back'}
          </Button>
        </Stack>

        <Box sx={{ px: 5.5, pt: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
            {draft ? draft.supplierName ?? anchor.partNumber : anchor.partNumber}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {draft ? `${draft.rfqNumber} · Draft, not sent yet${chosenBuild ? ` · ${chosenBuild.label}` : ''}` : anchor.description}
          </Typography>
          {/* A BOM with builds: the package quotes one build's lines at its quantities. */}
          {!draft && builds.length > 0 && (
            <Stack direction="row" role="radiogroup" aria-label="Build" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
              <Typography sx={LABEL_SX}>Build</Typography>
              {builds.map((b) => pill(`${b.label} · ${b.parts}`, b.key === chosenBuild?.key, () => pickBuild(b.key), b.key))}
              {needsBuild && (
                <Typography variant="caption" color="text.secondary">
                  Pick the build to quote
                </Typography>
              )}
            </Stack>
          )}
        </Box>

        <Box sx={{ mx: 5.5, mt: 2 }}>
          <QuoteSteps
            current={step}
            notes={step === 2 ? { 1: String(chosenCount) } : undefined}
            onGo={{
              1: () => setStep(1),
              ...(chosenCount > 0 && !needsBuild ? { 2: () => setStep(2), 3: () => void prepare() } : {}),
            }}
          />
        </Box>

        <Box sx={{ mx: 2.5, mt: 3.5, mb: 2.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, minWidth: 0 }}>
        {step === 1 ? suppliersStep : linesStep}

        {failure && <Alert severity="error" sx={{ mx: 2.5, mb: 2 }}>{failure}</Alert>}

        <Stack direction="row" sx={{ px: 2.5, py: 1.5, alignItems: 'center', gap: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          {step === 2 && (
            <Button onClick={() => setStep(1)} sx={{ textTransform: 'none' }}>
              Back
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {step === 2 && (
            <Typography variant="caption" color="text.secondary">
              Opens the draft email. Nothing is sent yet.
            </Typography>
          )}
          {step === 1 ? (
            <Button variant="contained" disabled={chosenCount === 0 || needsBuild} onClick={() => setStep(2)} sx={{ textTransform: 'none' }}>
              Next
            </Button>
          ) : (
            <Button variant="contained" disabled={chosenCount === 0 || preparing || needsBuild || waiting} onClick={prepare} sx={{ textTransform: 'none' }}>
              {preparing ? 'Preparing' : chosenCount > 1 ? `Preview ${chosenCount} drafts` : 'Preview'}
            </Button>
          )}
        </Stack>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default GetQuotesPage;
